import * as yup from "yup";
import type { BusinessType } from "../types";
import { MAX_BANNERS, bannersFromImages, ensureOneMain, normalizeBanners, type Banner } from "@/shared/upload/banners";

export { MAX_BANNERS, bannersFromImages, ensureOneMain, normalizeBanners };
export type { Banner };

/** Backend Joi (logics/business/create.ts): profileName and profileNameEng max 100. */
export const NAME_MAX = 100;
export const PROFILE_INFO_MAX = 500;

export type BusinessFormValues = {
  profileName: string;
  profileNameEng: string;
  profileInfo: string;
};

/**
 * Fields the form has no input for but the backend still wants in the body. They are read from
 * BusinessApi.get(id) and sent back unchanged, so a save never overwrites the stored value.
 */
export type BusinessExtras = {
  purchaseType: string;
  productCategoryType: string;
  productCategoryIds: string[];
  serviceCategoryType: string;
  serviceCategoryIds: string[];
};


export type MarketingState = {
  banners: Banner[];
  logo: string | null;
};

export const DEFAULT_VALUES: BusinessFormValues = {
  profileName: "",
  profileNameEng: "",
  profileInfo: "",
};

export const DEFAULT_EXTRAS: BusinessExtras = {
  purchaseType: "BOTH",
  productCategoryType: "LATER",
  productCategoryIds: [],
  serviceCategoryType: "LATER",
  serviceCategoryIds: [],
};

export const DEFAULT_MARKETING: MarketingState = { banners: [], logo: null };

type BusinessData = Record<string, any>;

export function toFormValues(data: BusinessData | null | undefined): BusinessFormValues {
  return {
    profileName: data?.profileName ?? "",
    profileNameEng: data?.profileNameEng ?? "",
    profileInfo: data?.profileInfo ?? "",
  };
}

export function toExtras(data: BusinessData | null | undefined): BusinessExtras {
  return {
    purchaseType: data?.purchaseType || DEFAULT_EXTRAS.purchaseType,
    productCategoryType: data?.productCategoryType || DEFAULT_EXTRAS.productCategoryType,
    productCategoryIds: Array.isArray(data?.productCategoryIds) ? data.productCategoryIds : [],
    serviceCategoryType: data?.serviceCategoryType || DEFAULT_EXTRAS.serviceCategoryType,
    serviceCategoryIds: Array.isArray(data?.serviceCategoryIds) ? data.serviceCategoryIds : [],
  };
}


export function toMarketing(data: BusinessData | null | undefined): MarketingState {
  return { banners: normalizeBanners(data?.profileBanners), logo: data?.logo ?? null };
}

export function isMarketingEqual(a: MarketingState, b: MarketingState): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function buildSchema(tCommon: (key: string, opts?: Record<string, unknown>) => string) {
  return yup.object({
    profileName: yup
      .string()
      .required(tCommon("required"))
      .matches(/^[А-Яа-яҮүӨөЁё0-9\s.-]{2,100}$/, {
        message: tCommon("validation.cyrillicOnlyShort"),
        excludeEmptyString: true,
      }),
    profileNameEng: yup
      .string()
      .transform((value) => (value === "" ? null : value))
      .matches(/^[A-Za-z\s-.]{2,100}$/, tCommon("validation.latinOnlyShort"))
      .nullable()
      .optional(),
    profileInfo: yup
      .string()
      .max(PROFILE_INFO_MAX, tCommon("validation.maxChars", { count: PROFILE_INFO_MAX }))
      .trim()
      .required(tCommon("required")),
  });
}

/**
 * POST / PUT /prt/business(/:id) body. Service category fields are always sent; the product ones
 * only unless purchaseType is SERVICE (what the backend Joi accepts).
 */
export function buildPayload(
  type: BusinessType,
  values: BusinessFormValues,
  extras: BusinessExtras,
  isConfirmed: boolean,
  creating: boolean,
) {
  const payload: Record<string, unknown> = {
    purchaseType: extras.purchaseType || "BOTH",
    serviceCategoryType: extras.serviceCategoryType,
    serviceCategoryIds: extras.serviceCategoryIds,
    type,
    profileName: values.profileName,
    profileNameEng: values.profileNameEng || "",
    profileInfo: values.profileInfo,
    isConfirmed,
  };

  // The business table has no VAT column: nothing is stored. POST /prt/business still lists
  // isVatPayer as required in its Joi, so a create always sends true; PUT takes it as optional.
  if (creating) payload.isVatPayer = true;

  if (extras.purchaseType !== "SERVICE") {
    payload.productCategoryType = extras.productCategoryType;
    payload.productCategoryIds = extras.productCategoryIds;
  }
  return payload;
}
